import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { ArrowLeft, ArrowRight, CheckCircle2, Lock, Play } from "lucide-react";

export default function CourseDetail() {
  const { authAxios } = useAuth();
  const { tr, lang } = useLang();
  const { courseId } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`/academy/courses/${courseId}?lang=${lang}`);
      setC(r.data);
    } catch (e) {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [courseId, lang]);

  if (loading) return (
    <div className="min-h-screen flex flex-col"><Header /><main className="flex-1 max-w-6xl mx-auto w-full px-6 py-16 text-[#a9a9b8]">{tr("loading")}</main></div>
  );
  if (!c) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-10">
        <Link to="/academy" data-testid="course-back" className="inline-flex items-center gap-2 text-[#a9a9b8] hover:text-[#e6c687] text-sm">
          <ArrowLeft size={14} /> {tr("nav_academy")}
        </Link>
        <div className="mt-6">
          <div className="pfi-overline">{c.level} · {c.duration}</div>
          <h1 className="mt-3 font-serif text-3xl sm:text-5xl">{c.title}</h1>
          <p className="mt-4 text-[#a9a9b8] max-w-2xl">{c.subtitle}</p>
        </div>

        {/* Progress */}
        <div className="mt-8 pfi-card p-5">
          <div className="flex items-end justify-between">
            <div className="pfi-overline">{tr("academy_progress")}</div>
            <div className="font-mono text-[#e6c687]">{c.progress_percent}%</div>
          </div>
          <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#e6c687] to-[#d4af37]" style={{ width: `${c.progress_percent}%` }} />
          </div>
        </div>

        {/* Modules */}
        <div className="mt-10 space-y-6">
          {c.modules.map((m, mi) => (
            <div key={m.module_id} className="pfi-card p-6" data-testid={`module-${m.module_id}`}>
              <div className="pfi-overline">Module {mi + 1}</div>
              <div className="mt-2 font-serif text-xl">{m.title}</div>
              <div className="mt-4 space-y-2">
                {m.lessons.map((l, li) => (
                  <LessonRow key={l.lesson_id} l={l} courseId={c.course_id} index={li + 1} tr={tr} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function LessonRow({ l, courseId, index, tr }) {
  const disabled = l.locked;
  return (
    <div
      data-testid={`lesson-row-${l.lesson_id}`}
      className={
        "flex items-center justify-between gap-4 rounded-xl px-4 py-3 border border-white/5 " +
        (disabled ? "opacity-50" : "hover:border-[#d4af37]/30")
      }
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="pfi-overline w-8 text-right">{String(index).padStart(2, "0")}</span>
        {l.completed ? (
          <CheckCircle2 size={16} className="text-[#10B981]" />
        ) : disabled ? (
          <Lock size={14} className="text-[#6c6c7a]" />
        ) : (
          <Play size={14} className="text-[#e6c687]" />
        )}
        <div className="min-w-0">
          <div className="text-sm text-[#f8f8fc] truncate">{l.title}</div>
          <div className="text-[11px] text-[#6c6c7a] font-mono">{l.duration}</div>
        </div>
      </div>
      {disabled ? (
        <span className="text-[11px] font-mono text-[#6c6c7a]">{tr("academy_locked")}</span>
      ) : (
        <Link
          to={`/academy/${courseId}/lesson/${l.lesson_id}`}
          data-testid={`lesson-open-${l.lesson_id}`}
          className="text-[#e6c687] hover:text-[#f8f8fc] text-xs inline-flex items-center gap-1"
        >
          {l.completed ? tr("academy_completed") : tr("academy_open")} <ArrowRight size={12} />
        </Link>
      )}
    </div>
  );
}
